import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useGlobalSearchParams, useLocalSearchParams, useRouter, type Href } from 'expo-router';
import { useCallback, useEffect, useRef } from 'react';
import { ScrollView, type LayoutChangeEvent } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { AboutSection } from '@/components/profile/AboutSection';
import { AnonymousProfileView } from '@/components/profile/AnonymousProfileView';
import { ClinicalTrialsSection } from '@/components/profile/ClinicalTrialsSection';
import { DisclosuresSection } from '@/components/profile/DisclosuresSection';
import { EducationSection } from '@/components/profile/EducationSection';
import { ProfessionalSection } from '@/components/profile/ProfessionalSection';
import { ProfileChips } from '@/components/profile/ProfileChips';
import { PublicationsSection } from '@/components/profile/PublicationsSection';
import { WorkHistorySection } from '@/components/profile/WorkHistorySection';
import { Spinner } from '@/components/ui/spinner';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import { useExpertProfileData } from '@/hooks/useExpertProfileData';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { segmentClient } from '@/util/analytics';
import { profileTrialCounts } from '@/util/profileTrialCounts';

/** DOM ids used as scroll targets for the header research chips. */
const SECTION_IDS = {
  about: 'profile-about',
  work: 'profile-work',
  education: 'profile-education',
  publications: 'profile-publications',
  trials: 'profile-trials',
  disclosures: 'profile-disclosures',
} as const;

type SectionId = (typeof SECTION_IDS)[keyof typeof SECTION_IDS];

// Small breathing gap above a section when a header chip scrolls to it.
const SCROLL_MARGIN_PX = 16;

const SCROLL_OFFSET_CLASS = 'web:[scroll-margin-top:16px]';

type SectionParam = 'publications' | 'trials';

const SECTION_PARAM_TO_ID: Record<SectionParam, SectionId> = {
  publications: SECTION_IDS.publications,
  trials: SECTION_IDS.trials,
};

/**
 * Reports a profile view that began outside the app, once per arrival.
 *
 * The in-app entry points already report their own views and name their source, so firing
 * unconditionally here would count every one of those twice. A link that arrives from a
 * notification, an email or anywhere else external has no such entry point to speak for it,
 * and declares itself with a `source` parameter instead. That parameter is the whole
 * condition: no parameter means the navigation was internal and already counted.
 *
 * Reads the parameter rather than inferring external arrival from navigation state, because
 * a cold start launched by a notification has no prior route to compare against.
 *
 * The longer-term shape is for this screen to own the event outright and for entry points to
 * pass their source through the route, which would remove the split and the double-count
 * hazard together. That is a change to every existing call site, so it is not made here.
 */
function useProfileViewedFromOutsideApp(source: string | undefined) {
  const reportedSource = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!source || reportedSource.current === source) {
      return;
    }

    reportedSource.current = source;

    segmentClient.track(ANALYTICS_EVENTS.PROFILE_VIEWED, { source });
  }, [source]);
}

const UserProfile = ({ userId }: { userId?: string } = {}) => {
  const {
    id: localId,
    section,
    source,
  } = useLocalSearchParams<{
    id: string;
    section?: string;
    source?: string;
  }>();

  /*
   * The local params lose the id when this screen is reached by the router's own fallback
   * rather than by a navigation to it — which is what the ownership guard produces: it
   * removes the edit route a non-owner asked for, the stack falls back to this screen, and
   * the dynamic segment does not come along. The id is still in the address the visitor is
   * on, and the global params are read from that, so they still have it. Without this the
   * query has no id, fires nothing, and renders an empty shell on a URL that names an
   * expert.
   */
  const globalParams = useGlobalSearchParams<{ id?: string }>();

  const id = userId ?? localId ?? globalParams.id;

  const deepLinkSection: SectionParam | undefined =
    section === 'publications' || section === 'trials' ? section : undefined;

  useProfileViewedFromOutsideApp(source);

  const {
    userData,
    isLoading,
    isOwnProfile,
    isAnonymous,
    nameWithoutDoctor,
    cancerFocusAreas,
    subspecialtyItems,
    responseTimeLabel,
    bioSections,
  } = useExpertProfileData(id);

  const institutionalTrialsEnabled = useFeatureFlag(FEATURE_FLAGS.INSTITUTIONAL_TRIALS);

  const router = useRouter();

  // Rendered explicitly after Publications and Clinical Trials so the
  // credentials cluster reads work history → education → research → disclosures.
  const disclosuresSection = bioSections.find((section) => section.title === 'Disclosures');

  const numericId = userData?.id;

  const showPublicationsAndTrials = !!numericId;

  const aboutSection = bioSections.find((section) => section.title === 'About Me');

  const workHistorySection = bioSections.find((section) => section.title === 'Work History');

  const educationSection = bioSections.find((section) => section.title === 'Education & Training');

  // Track each section's y offset within the ScrollView so chip taps can
  // scroll to it on native. On web we still prefer the DOM-id path because
  // scrollIntoView accounts for sticky headers and scroll-margin-top.
  const scrollViewRef = useRef<ScrollView>(null);

  const sectionOffsets = useRef<Partial<Record<SectionId, number>>>({});

  // Becomes true after the deep-link auto-scroll fires, so onLayout doesn't
  // keep yanking the user back to the same section on re-layout.
  const hasAutoScrolledRef = useRef(false);

  /**
   * Forgets the parameter once it has been acted on, which is what lets a second
   * deep link into an already-open profile scroll again.
   *
   * Tapping a notification while this screen is mounted arrives with the same
   * parameter it already handled, so without clearing there is no change to
   * react to and the guard above is still set from the first time. The scroll
   * silently does not happen, and the reader is left wherever they were.
   */
  const consumeDeepLinkSection = useCallback(() => {
    hasAutoScrolledRef.current = true;

    router.setParams({ section: undefined });
  }, [router]);

  // Re-arms the guard when the parameter goes away, so the next arrival is
  // treated as a fresh instruction rather than a repeat of the last one.
  useEffect(() => {
    if (!deepLinkSection) {
      hasAutoScrolledRef.current = false;
    }
  }, [deepLinkSection]);

  const trackSectionOffset = useCallback(
    (sectionId: SectionId) => (event: LayoutChangeEvent) => {
      sectionOffsets.current[sectionId] = event.nativeEvent.layout.y;

      if (
        !isWeb &&
        !hasAutoScrolledRef.current &&
        deepLinkSection &&
        SECTION_PARAM_TO_ID[deepLinkSection] === sectionId
      ) {
        consumeDeepLinkSection();

        scrollViewRef.current?.scrollTo({
          y: Math.max(0, event.nativeEvent.layout.y - SCROLL_MARGIN_PX),
          animated: true,
        });
      }
    },
    [deepLinkSection, consumeDeepLinkSection]
  );

  /**
   * Native, for a section whose position is already known.
   *
   * The handler above only runs on layout, which happens when the screen mounts
   * and then not again. A second deep link into a profile that is already open
   * has nothing to lay out, so without this the parameter arrives, is correct,
   * and moves nothing: observed on a device, tapping a notification from
   * elsewhere scrolled and tapping one while already on the profile did not.
   *
   * An unknown offset means the section has not been laid out yet, which is the
   * first-arrival case the layout handler covers.
   */
  useEffect(() => {
    if (isWeb || !deepLinkSection || hasAutoScrolledRef.current) {
      return;
    }

    const offset = sectionOffsets.current[SECTION_PARAM_TO_ID[deepLinkSection]];

    if (offset === undefined) {
      return;
    }

    consumeDeepLinkSection();

    scrollViewRef.current?.scrollTo({
      y: Math.max(0, offset - SCROLL_MARGIN_PX),
      animated: true,
    });
  }, [deepLinkSection, consumeDeepLinkSection]);

  // Web: scrollIntoView works as soon as the section View mounts; no need to
  // wait for onLayout.
  useEffect(() => {
    if (!isWeb || !deepLinkSection || hasAutoScrolledRef.current || isLoading) {
      return;
    }

    consumeDeepLinkSection();

    document
      .getElementById(SECTION_PARAM_TO_ID[deepLinkSection])
      ?.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [deepLinkSection, isLoading, consumeDeepLinkSection]);

  const scrollToSection = (sectionId: SectionId) => {
    if (isWeb) {
      // 'auto' (instant) rather than 'smooth': smooth scrolling is not honored
      // by the nested react-native-web scroll container, so it would no-op.
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'auto', block: 'start' });

      return;
    }

    const offset = sectionOffsets.current[sectionId];

    if (offset === undefined) {
      return;
    }

    scrollViewRef.current?.scrollTo({
      y: Math.max(0, offset - SCROLL_MARGIN_PX),
      animated: true,
    });
  };

  const scrollToPublications = () => scrollToSection(SECTION_IDS.publications);

  const scrollToTrials = () => scrollToSection(SECTION_IDS.trials);

  const trialCounts = profileTrialCounts(userData);

  /** Institutional trials count only while the flag shows them. */
  const institutionalShown = institutionalTrialsEnabled ? trialCounts.institutional : 0;

  /** Opens the professional info editor, from the section's pencil or the header's. */
  const editSummary = () => {
    Promise.resolve(
      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
        editType: 'summary',
      })
    ).catch(() => undefined);

    if (userData?.id) {
      router.navigate(ROUTES.ACCOUNT_PROFILE_EDIT('professional'));
    }
  };

  /**
   * The native bar names whose profile this is: the reader's own reads as the Account row
   * that opens it, like the other Account screens, and anyone else's is titled with their
   * name. Empty until the profile loads, so the bar never shows a placeholder name.
   */
  const getHeaderTitle = () => {
    if (!userData) {
      return '';
    }

    return isOwnProfile ? 'Profile Details' : nameWithoutDoctor;
  };

  const headerTitle = getHeaderTitle();

  /**
   * An expert's profile offers the inquiry in the bar, where it stays in reach as the profile
   * scrolls. Not on the reader's own profile, and not on the anonymous one, which has its own
   * way in. Web keeps the button in the profile body.
   *
   * It navigates rather than pushes, as the editor below does: a second press landing before
   * the first screen has finished arriving then finds that screen already on top and adds
   * nothing, where a push stacked a second composer under the first.
   */
  const canStartInquiry = Boolean(userData?.id) && !isOwnProfile && !isAnonymous && !isWeb;

  const startInquiry = () => {
    Promise.resolve(
      segmentClient.track(ANALYTICS_EVENTS.BIO_MESSAGE_SELECTED, { expert_id: id })
    ).catch(() => undefined);

    router.navigate(`/directory/inquiry/${userData?.id?.toString() ?? ''}` as Href);
  };

  const content =
    userData && isAnonymous ? (
      <AnonymousProfileView userId={userData.id || ''} expertId={id || ''} />
    ) : (
      <ScrollView
        ref={scrollViewRef}
        className="w-full flex-1"
        contentInsetAdjustmentBehavior="automatic"
        contentContainerStyle={{ paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        <ProfessionalSection
          userData={userData}
          isOwnProfile={isOwnProfile}
          nameWithoutDoctor={nameWithoutDoctor}
          responseTimeLabel={responseTimeLabel}
          cancerFocusAreas={cancerFocusAreas}
          subspecialtyItems={subspecialtyItems}
          onEditSummary={editSummary}
          isEditInHeader={!isWeb}
          onSendMessage={() => {
            segmentClient.track(ANALYTICS_EVENTS.BIO_MESSAGE_SELECTED, {
              expert_id: id,
            });
          }}
          messageUrl={`/directory/inquiry/${userData?.id?.toString() || ''}`}
          chipSlot={
            showPublicationsAndTrials && numericId ? (
              <ProfileChips
                expertId={numericId}
                publicationsCount={userData?.publicationsCount ?? 0}
                clinicalTrialsCount={trialCounts.own + institutionalShown}
                onSelect={(target) =>
                  scrollToSection(
                    target === 'publications' ? SECTION_IDS.publications : SECTION_IDS.trials
                  )
                }
              />
            ) : null
          }
        />

        {aboutSection ? (
          <View nativeID={SECTION_IDS.about} className={SCROLL_OFFSET_CLASS}>
            <AboutSection
              section={aboutSection}
              isFirstSection
              isOwnProfile={isOwnProfile}
              onEdit={() => {
                segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                  editType: 'summary',
                });

                if (userData?.id) {
                  router.push(ROUTES.ACCOUNT_PROFILE_EDIT('about'));
                }
              }}
            />
          </View>
        ) : null}

        {showPublicationsAndTrials && numericId && (userData?.publicationsCount ?? 0) > 0 ? (
          <View
            nativeID={SECTION_IDS.publications}
            className={twJoin('mt-10', SCROLL_OFFSET_CLASS)}
            onLayout={trackSectionOffset(SECTION_IDS.publications)}>
            <PublicationsSection expertId={numericId} onCollapseScroll={scrollToPublications} />
          </View>
        ) : null}

        {showPublicationsAndTrials && numericId && trialCounts.own + institutionalShown > 0 ? (
          <View
            nativeID={SECTION_IDS.trials}
            className={twJoin('mt-10', SCROLL_OFFSET_CLASS)}
            onLayout={trackSectionOffset(SECTION_IDS.trials)}>
            <ClinicalTrialsSection
              expertId={numericId}
              institutionalCount={institutionalShown}
              institutionName={userData?.institutionOrganizationName}
              onCollapseScroll={scrollToTrials}
            />
          </View>
        ) : null}

        {workHistorySection ? (
          <View nativeID={SECTION_IDS.work} className={twJoin('mt-10', SCROLL_OFFSET_CLASS)}>
            <WorkHistorySection
              section={workHistorySection}
              isFirstSection={false}
              isOwnProfile={isOwnProfile}
              onEdit={() => {
                segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                  editType: 'work-history',
                });

                if (userData?.id) {
                  router.push(ROUTES.ACCOUNT_PROFILE_EDIT('work-history'));
                }
              }}
            />
          </View>
        ) : null}

        {educationSection ? (
          <View nativeID={SECTION_IDS.education} className={twJoin('mt-10', SCROLL_OFFSET_CLASS)}>
            <EducationSection
              section={educationSection}
              isFirstSection={false}
              isOwnProfile={isOwnProfile}
              onEdit={() => {
                segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                  editType: 'education',
                });

                if (userData?.id) {
                  router.push(ROUTES.ACCOUNT_PROFILE_EDIT('education'));
                }
              }}
            />
          </View>
        ) : null}

        {disclosuresSection ? (
          <View nativeID={SECTION_IDS.disclosures} className={twJoin('mt-10', SCROLL_OFFSET_CLASS)}>
            <DisclosuresSection
              section={disclosuresSection}
              isFirstSection={false}
              isOwnProfile={isOwnProfile}
              onEdit={() => {
                segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
                  editType: 'disclosures',
                });

                if (userData?.id) {
                  router.push(ROUTES.ACCOUNT_PROFILE_EDIT('disclosures'));
                }
              }}
            />
          </View>
        ) : null}
      </ScrollView>
    );

  return (
    <VStack className="w-full flex-1">
      <Header
        isBackButton
        backRoute={isOwnProfile ? ROUTES.ACCOUNT : ROUTES.DIRECTORY}
        contentScrollsUnderHeader
        title={headerTitle}
        documentTitle={headerTitle}
        rightItems={[
          headerItem.edit('Edit professional info', editSummary, !isOwnProfile || isWeb),
          headerItem.startInquiry(
            `Start an inquiry with ${nameWithoutDoctor}`,
            startInquiry,
            !canStartInquiry
          ),
        ]}
      />

      <VStack className="flex-1">
        {isLoading ? (
          <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0" />
        ) : (
          content
        )}
      </VStack>
    </VStack>
  );
};

export default UserProfile;
